import Api from "@/api"
import { IncidentType, User, VtsCorrespondent, VtsRemarkTemplate, type Id, type SearchAndRescueUnit } from "@/models"
import { toReactive } from "@vueuse/core"
import { defineStore } from "pinia"
import { computed, toRef, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue"
import {
	assignLoadable,
	defineGlobals,
	ensureLoadingHasResult,
	mergePromises,
	piniaLoadableState,
	useLoadableRef,
	type Loadable,
} from "vue-utils"

export interface CoreData {
	searchAndRescueUnits: SearchAndRescueUnit[]
	incidentTypes: IncidentType[]
	vtsCorrespondents: VtsCorrespondent[]
	vtsRemarkTemplates: VtsRemarkTemplate[]
	loggedInUser: User
	users: Map<Id, User>
}

async function loadData(): Promise<CoreData> {
	const mainData = await mergePromises({
		searchAndRescueUnits: Api.data.getSearchAndRescueUnits(),
		incidentTypes: Api.data.getIncidentTypes(),
		vtsCorrespondents: Api.data.getVtsCorrespondents(),
		vtsRemarkTemplates: Api.data.getVtsRemarkTemplates(),
		loggedInUser: Api.users.getLoggedIn(),
		users: Api.users.getAll(),
	})

	const usersById = new Map<Id, User>()
	for (const user of mainData.users) {
		usersById.set(user.id, user)
	}

	return {
		...mainData,
		users: usersById,
	}
}

export const useCoreDataStore = defineStore("core-data", {
	state: () => piniaLoadableState(loadData),
	actions: {
		data(): CoreData {
			return ensureLoadingHasResult(this)
		},
		lookupUser(id: Id): User | null {
			return this.data().users.get(id) ?? null
		},
		lookupSUR(id: Id): SearchAndRescueUnit | null {
			return this.data().searchAndRescueUnits.find((sru) => sru.id == id) ?? null
		},
		lookupIncidentType(id: Id): IncidentType | null {
			return this.data().incidentTypes.find((type) => type.id == id) ?? null
		},
		lookupVtsCorrespondent(id: Id): VtsCorrespondent | null {
			return this.data().vtsCorrespondents.find((correspondent) => correspondent.id == id) ?? null
		},
		lookupVtsRemarkTemplate(id: Id): VtsRemarkTemplate | null {
			return this.data().vtsRemarkTemplates.find((template) => template.id == id) ?? null
		},
	},
})

export function useCoreData(): CoreData {
	const store = useCoreDataStore()
	return toReactive(toRef(() => ensureLoadingHasResult(store)))
}

export function createCoreDataRefreshRef(): Loadable<void> {
	const dataStore = useCoreDataStore()
	return useLoadableRef(async () => {
		const data = await dataStore.queryData()
		assignLoadable(dataStore, {
			type: "done",
			result: data,
		})
	})
}

export function useUser(userId: MaybeRefOrGetter<Id | null | undefined>): ComputedRef<User | null> {
	const store = useCoreDataStore()
	return computed(() => {
		const actualId = toValue(userId)
		if (typeof actualId === "number") {
			return store.lookupUser(actualId)
		}
		return null
	})
}

export function useLoggedInUser(): User {
	const store = useCoreDataStore()
	return toReactive(toRef(() => store.data().loggedInUser))
}

defineGlobals({
	useCoreData,
	useCoreDataStore,
	useLoggedInUser,
})
